import { DeleteOutline } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../../../FormFields/AddMoreButton';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { ENVIRONMENTAL } from '../../../formQuestionsEnum';
import { OWNERSHIP_TYPES } from '../../../optionEnums';

EquipmentRentals.propTypes = {
  isEditable: PropTypes.bool,
};

export default function EquipmentRentals({ isEditable }) {
  const [focusedField, setFocusedField] = useState(null);
  const { control, watch, setValue } = useFormContext();
  const {
    data: { ownershipTypesList, isCanceled },
  } = useSelector((state) => state.transitions);

  const baseFieldName = ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT_RENTALS.fieldName;
  const equipmentsFieldName = `${baseFieldName}.equipment`;
  const hasEquipmentRentals = watch(`${baseFieldName}.hasRentals`);
  const disabled = !isEditable || isCanceled;

  const { fields, append, remove } = useFieldArray({
    control,
    name: equipmentsFieldName,
    keyName: '_id',
  });

  const watchedValues = useWatch({ control, name: equipmentsFieldName }) || [];

  const handleDelete = (idx) => {
    const rowId = watchedValues[idx]?.id;

    if (!rowId) {
      remove(idx);
      return;
    }

    setValue(`${equipmentsFieldName}[${idx}].deleteFlag`, true, {
      shouldDirty: true,
    });
  };

  const visibleRows = fields.map((field, idx) => ({ field, idx })).filter(({ idx }) => !watchedValues[idx]?.deleteFlag);

  return (
    hasEquipmentRentals && (
      <>
        {visibleRows.map(({ field, idx }) => (
          <Grid container item xs={12} spacing={1} key={field._id} alignItems="center" sx={{ mb: 1 }}>
            <Grid container item spacing={1.5} xs={visibleRows.length > 1 ? 11.5 : 12}>
              <Grid item xs={12} md={4}>
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Equipment type' }} />
                  <FormTextField
                    fieldName={`${equipmentsFieldName}[${idx}].type`}
                    fieldAttributes={{ isEditable: !disabled }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </FieldContainer>
              </Grid>

              <Grid item xs={12} md>
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Vendor name' }} />
                  <FormTextField
                    fieldName={`${equipmentsFieldName}[${idx}].vendorName`}
                    fieldAttributes={{ isEditable: !disabled }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </FieldContainer>
              </Grid>

              <Grid item xs={12} md="auto">
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Is it rented or leased?' }} />
                  <FormButtonOptions
                    fieldName={`${equipmentsFieldName}[${idx}].ownershipTypeId`}
                    fieldAttributes={{
                      isEditable: !disabled,
                      options: ownershipTypesList.filter(
                        (x) => x.value === OWNERSHIP_TYPES.LEASED || x.value === OWNERSHIP_TYPES.RENTAL
                      ),
                    }}
                  />
                </FieldContainer>
              </Grid>
            </Grid>

            {visibleRows.length > 1 && (
              <Grid item xs={0.5}>
                <IconButton onClick={() => handleDelete(idx)} disabled={disabled} color="error">
                  <DeleteOutline />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        <AddMoreButton
          buttonLabel="Add more"
          handleAddMoreClick={() =>
            append({
              id: 0,
              ownershipTypeId: null,
              type: null,
              vendorName: null,
            })
          }
          disabled={disabled}
        />
      </>
    )
  );
}
